import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, List, ListItemButton, ListItemText, ListItemIcon,
  Typography, Chip, InputAdornment, CircularProgress, Alert, Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import { apiService } from '../../services/apiService';

const targetTypeIcons = {
  exoplanet: '🪐',
  variable_star: '⭐',
  asteroid: '☄️',
  deep_sky: '🌌',
  custom: '📍',
};

const targetTypeColors = {
  exoplanet: 'warning',
  variable_star: 'info',
  asteroid: 'secondary',
  deep_sky: 'primary',
  custom: 'default',
};

const LibraryTargetSelector = ({ open, onClose, onSelect }) => {
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selectedTarget, setSelectedTarget] = useState(null);

  useEffect(() => {
    if (open) {
      loadTargets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadTargets = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (search) params.search = search;
      const response = await apiService.getLibraryTargets(params);
      setTargets(response.data.targets || []);
    } catch (err) {
      console.error('Failed to load library targets:', err);
      setError('Failed to load target library');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (open) loadTargets();
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, open]);

  const handleSelect = () => {
    if (selectedTarget) {
      // Transform to same format the old TargetLibrarySelector used
      const formTarget = {
        name: selectedTarget.name,
        ra: selectedTarget.ra.toString(),
        dec: selectedTarget.dec.toString(),
        priority: 5,
        sequenceRepeatCount: 1,
        exposureConfigs: selectedTarget.exposure_configs?.map(exp => ({
          filter: exp.filter,
          exposureTime: exp.exposure_time,
          exposureCount: exp.exposure_count,
          binning: exp.binning?.toString() || '1',
          gain: exp.gain ?? null,
          offset: exp.offset ?? null,
        })) || [],
        savedTargetId: selectedTarget._id,
        savedConstraints: selectedTarget.constraints,
        savedGuidingOptions: selectedTarget.guiding_options,
        savedAutofocusOptions: selectedTarget.autofocus_options,
      };
      onSelect(formTarget);
      onClose();
      setSelectedTarget(null);
      setSearch('');
    }
  };

  const formatRA = (decimal) => {
    if (!decimal && decimal !== 0) return '';
    const hours = decimal / 15;
    const h = Math.floor(hours);
    const m = Math.floor((hours - h) * 60);
    const s = ((hours - h) * 60 - m) * 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toFixed(1).padStart(4, '0')}`;
  };

  const formatDec = (decimal) => {
    if (!decimal && decimal !== 0) return '';
    const sign = decimal < 0 ? '-' : '+';
    const abs = Math.abs(decimal);
    const d = Math.floor(abs);
    const m = Math.floor((abs - d) * 60);
    const s = ((abs - d) * 60 - m) * 60;
    return `${sign}${d.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toFixed(1).padStart(4, '0')}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StarIcon color="primary" />
          Select from Target Library
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, mt: 1 }}>
          <TextField
            fullWidth size="small" placeholder="Search targets..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
            }}
          />
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : targets.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {search ? 'No targets found matching your search' : 'No saved targets yet'}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Use the Target Library page to save targets for quick access
            </Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {targets.map((target) => (
              <ListItemButton
                key={target._id}
                selected={selectedTarget?._id === target._id}
                onClick={() => setSelectedTarget(target)}
                sx={{
                  borderRadius: 1, mb: 0.5,
                  border: selectedTarget?._id === target._id ? 2 : 1,
                  borderColor: selectedTarget?._id === target._id ? 'primary.main' : 'divider',
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, fontSize: '1.2rem' }}>
                  {targetTypeIcons[target.target_type] || '📍'}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2">{target.name}</Typography>
                      <Chip
                        size="small" label={target.target_type.replace('_', ' ')}
                        color={targetTypeColors[target.target_type]}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box component="span">
                      <Typography variant="caption" color="text.secondary" component="span">
                        RA: {formatRA(target.ra)} | Dec: {formatDec(target.dec)}
                      </Typography>
                      {target.exposure_configs?.length > 0 && (
                        <Typography variant="caption" color="text.secondary" component="span" sx={{ display: 'block' }}>
                          {target.exposure_configs.length} config{target.exposure_configs.length > 1 ? 's' : ''} | Filters: {target.exposure_configs.map(e => e.filter).join(', ')}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}

        {selectedTarget && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Selected: {selectedTarget.name}
            </Typography>
            <Box sx={{ pl: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Coordinates:</strong> RA {formatRA(selectedTarget.ra)}, Dec {formatDec(selectedTarget.dec)}
              </Typography>
              {selectedTarget.exposure_configs?.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Exposures:</strong> {selectedTarget.exposure_configs.map(e =>
                    `${e.filter}: ${e.exposure_count}x${e.exposure_time}s`
                  ).join(', ')}
                </Typography>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSelect} disabled={!selectedTarget}>
          Select Target
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LibraryTargetSelector;
